import { useState, useEffect, useRef } from "react";
import { Button, Badge, Spinner, Table, Tooltip } from "flowbite-react";
import {
  HiRefresh,
  HiQuestionMarkCircle,
  HiPlus,
  HiPencil,
  HiTrash,
  HiCheck,
  HiX,
  HiChevronDown,
  HiChevronUp,
  HiSearch,
} from "react-icons/hi";
import { useProductUsageConfig, useAuth, useToast } from "../../../../hooks";
import { useIntegrations } from "../../../../contexts/IntegrationsContext";
import {
  NOTIFICATION_TYPES,
  getTimeDifferenceApproximation,
} from "../../../../utils";
import { MetricModal } from "./MetricModal";
import { WidgetConfig } from "./WidgetConfig";

const formatTimeAgo = (date) => {
  const approx = getTimeDifferenceApproximation(date);
  const [unit, value] = Object.entries(approx)[0];
  if (value === 1) return `1 ${unit.replace(/s$/, "")} ago`;
  return `${value} ${unit} ago`;
};

const humanize = (eventName) =>
  eventName
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_\-.]+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Post-connection view for a usage-data integration.
 *
 * Shows auto-configured metrics (created by backend auto-setup) and allows
 * adding custom metrics. Replaces the old manual configuration form.
 */
const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 20; // Stop after 60s

export const UsageMetricsForm = ({
  integration,
  providerName = "your provider",
}) => {
  const { apiClient } = useAuth();
  const showToast = useToast();
  const { fetchAllIntegrations } = useIntegrations();
  const pollCountRef = useRef(0);

  // Poll for sync completion when status is IN_PROGRESS or PENDING
  const isSyncing =
    integration?.sync_status === "IN_PROGRESS" ||
    integration?.sync_status === "PENDING";

  useEffect(() => {
    if (!isSyncing) {
      pollCountRef.current = 0;
      return;
    }

    const interval = setInterval(() => {
      pollCountRef.current += 1;
      if (pollCountRef.current >= MAX_POLLS) {
        clearInterval(interval);
        return;
      }
      fetchAllIntegrations();
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isSyncing, fetchAllIntegrations]);

  const {
    metricDefinitions,
    metricsLoading,
    createMetric,
    updateMetric,
    deleteMetric,
    widgetConfig,
    widgetLoading,
    widgetSaving,
    saveWidgetConfig,
    availableEvents,
    error,
    refetch,
    discoveredEvents,
    discoveredLoading,
    promoteEvent,
    dismissEvent,
    promotingId,
    dismissingId,
  } = useProductUsageConfig(integration?.id);

  const [showModal, setShowModal] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [rerunning, setRerunning] = useState(false);

  const handleRerunSetup = async () => {
    setRerunning(true);
    try {
      await apiClient.post(`usage/integrations/${integration.id}/auto-setup/`);
      showToast({
        content:
          "Auto-setup triggered. Refresh the page in a few seconds to see new metrics.",
        type: NOTIFICATION_TYPES.success,
      });
      refetch?.();
    } catch (err) {
      console.error("Failed to trigger auto-setup:", err);
      showToast({
        content: "Failed to run auto-setup. Please try again.",
        type: NOTIFICATION_TYPES.error,
      });
    } finally {
      setRerunning(false);
    }
  };

  const handleOpenCreate = () => {
    setEditingMetric(null);
    setShowModal(true);
  };

  const handleOpenEdit = (metric) => {
    setEditingMetric(metric);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this metric?")) {
      await deleteMetric(id);
    }
  };

  if (metricsLoading || widgetLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const engagementMetrics = metricDefinitions.filter(
    (m) => !m.is_feature_metric,
  );
  const featureMetrics = metricDefinitions.filter((m) => m.is_feature_metric);

  return (
    <div className="px-6 pb-4">
      {/* Error State */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {/* Sync Status */}
      {isSyncing && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800">
          <Spinner size="sm" />
          {`Syncing data from ${providerName}...`}
        </div>
      )}

      {/* Auto-configured Metrics */}
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Tracked Metrics
            </h3>
            <Tooltip
              content={`These metrics were automatically configured from your ${providerName} events. You can add custom metrics below.`}
            >
              <HiQuestionMarkCircle className="h-4 w-4 text-gray-400" />
            </Tooltip>
          </div>
          <div className="flex gap-2">
            <Button
              size="xs"
              color="light"
              onClick={handleRerunSetup}
              disabled={rerunning}
              isProcessing={rerunning}
            >
              <HiRefresh className="mr-1 h-3 w-3" />
              Re-detect
            </Button>
            <Button size="xs" color="light" onClick={handleOpenCreate}>
              <HiPlus className="mr-1 h-3 w-3" />
              Add Custom
            </Button>
          </div>
        </div>

        {metricDefinitions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 p-6 text-center">
            <p className="text-sm text-gray-500">
              No metrics configured yet. Click &quot;Re-detect&quot; to
              automatically set up metrics from your {providerName} events, or
              add a custom metric.
            </p>
          </div>
        ) : (
          <Table>
            <Table.Head>
              <Table.HeadCell>Metric</Table.HeadCell>
              <Table.HeadCell>Source Event</Table.HeadCell>
              <Table.HeadCell>Type</Table.HeadCell>
              <Table.HeadCell>Category</Table.HeadCell>
              <Table.HeadCell>
                <span className="sr-only">Actions</span>
              </Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {engagementMetrics.map((metric) => (
                <MetricRow
                  key={metric.id}
                  metric={metric}
                  category="Engagement"
                  onEdit={handleOpenEdit}
                  onDelete={handleDelete}
                />
              ))}
              {featureMetrics.map((metric) => (
                <MetricRow
                  key={metric.id}
                  metric={metric}
                  category="Feature"
                  onEdit={handleOpenEdit}
                  onDelete={handleDelete}
                />
              ))}
            </Table.Body>
          </Table>
        )}
      </div>

      {/* Discovered Events */}
      {!discoveredLoading && discoveredEvents.length > 0 && (
        <DiscoveredEventsSection
          events={discoveredEvents}
          onPromote={promoteEvent}
          onDismiss={dismissEvent}
          promotingId={promotingId}
          dismissingId={dismissingId}
        />
      )}

      {/* Widget Configuration */}
      <WidgetConfig
        widgetConfig={widgetConfig}
        metricDefinitions={metricDefinitions}
        widgetSaving={widgetSaving}
        onSave={saveWidgetConfig}
      />

      {/* Create/Edit Modal */}
      <MetricModal
        show={showModal}
        onClose={() => setShowModal(false)}
        editingMetric={editingMetric}
        availableEvents={availableEvents}
        onSubmit={async (formData) => {
          if (editingMetric) {
            await updateMetric(editingMetric.id, formData);
          } else {
            await createMetric(formData);
          }
          setShowModal(false);
        }}
      />
    </div>
  );
};

/**
 * Single metric row in the table.
 */
const MetricRow = ({ metric, category, onEdit, onDelete }) => (
  <Table.Row>
    <Table.Cell>
      <div className="flex items-center gap-2">
        <span className="font-medium text-gray-900">{metric.display_name}</span>
        {metric.unit && (
          <span className="text-xs text-gray-400">({metric.unit})</span>
        )}
        {metric.is_higher_better === false && (
          <Tooltip content="Lower is better for this metric">
            <Badge color="warning" size="xs">
              ↓
            </Badge>
          </Tooltip>
        )}
      </div>
    </Table.Cell>
    <Table.Cell className="font-mono text-xs text-gray-500">
      {metric.source_event_names?.length > 0
        ? metric.source_event_names.join(", ")
        : metric.name}
    </Table.Cell>
    <Table.Cell className="text-sm text-gray-600">
      {metric.metric_type}
    </Table.Cell>
    <Table.Cell>
      <Badge color={category === "Feature" ? "purple" : "blue"} size="sm">
        {category}
      </Badge>
    </Table.Cell>
    <Table.Cell>
      <div className="flex gap-1">
        <Button
          size="xs"
          color="light"
          onClick={() => onEdit(metric)}
          aria-label={`Edit ${metric.display_name}`}
        >
          <HiPencil className="h-3 w-3" />
        </Button>
        <Button
          size="xs"
          color="light"
          onClick={() => onDelete(metric.id)}
          aria-label={`Delete ${metric.display_name}`}
        >
          <HiTrash className="h-3 w-3" />
        </Button>
      </div>
    </Table.Cell>
  </Table.Row>
);

const INITIAL_VISIBLE = 5;

const DiscoveredEventsSection = ({
  events,
  onPromote,
  onDismiss,
  promotingId,
  dismissingId,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [filter, setFilter] = useState("");

  const filteredEvents = filter
    ? events.filter(
        (e) =>
          e.event_name.toLowerCase().includes(filter.toLowerCase()) ||
          e.normalized_name.toLowerCase().includes(filter.toLowerCase()),
      )
    : events;
  const visibleEvents = showAll
    ? filteredEvents
    : filteredEvents.slice(0, INITIAL_VISIBLE);
  const hasMore = filteredEvents.length > INITIAL_VISIBLE;

  return (
    <div className="mb-6">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold text-gray-900">
            Discovered Events
          </h3>
          <Tooltip content="Events found during sync that aren't tracked yet. Track them to start measuring, or dismiss to hide.">
            <HiQuestionMarkCircle className="h-4 w-4 text-gray-400" />
          </Tooltip>
          <Badge color="warning" size="sm">
            {events.length}
          </Badge>
        </div>
      </div>

      {events.length > INITIAL_VISIBLE && (
        <div className="relative mb-3">
          <HiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Filter events..."
            value={filter}
            onChange={(e) => {
              setFilter(e.target.value);
              setShowAll(true);
            }}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      )}

      <Table>
        <Table.Head>
          <Table.HeadCell>Event</Table.HeadCell>
          <Table.HeadCell>Last seen</Table.HeadCell>
          <Table.HeadCell>
            <span className="sr-only">Actions</span>
          </Table.HeadCell>
        </Table.Head>
        <Table.Body className="divide-y">
          {visibleEvents.map((event) => (
            <DiscoveredEventRow
              key={event.id}
              event={event}
              onPromote={onPromote}
              onDismiss={onDismiss}
              isPromoting={promotingId === event.id}
              isDismissing={dismissingId === event.id}
            />
          ))}
        </Table.Body>
      </Table>

      {filteredEvents.length === 0 && filter && (
        <p className="mt-2 text-center text-sm text-gray-500">
          No events matching &quot;{filter}&quot;
        </p>
      )}

      {hasMore && !filter && (
        <button
          type="button"
          className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800"
          onClick={() => setShowAll(!showAll)}
        >
          <span className="flex items-center gap-1">
            {showAll ? (
              <>
                Show less <HiChevronUp className="h-4 w-4" />
              </>
            ) : (
              <>
                Show {filteredEvents.length - INITIAL_VISIBLE} more{" "}
                <HiChevronDown className="h-4 w-4" />
              </>
            )}
          </span>
        </button>
      )}
    </div>
  );
};

const DiscoveredEventRow = ({
  event,
  onPromote,
  onDismiss,
  isPromoting,
  isDismissing,
}) => (
  <Table.Row>
    <Table.Cell>
      <div className="flex flex-col">
        <span className="text-sm font-medium text-gray-900">
          {humanize(event.event_name)}
        </span>
        <span className="font-mono text-xs text-gray-400">
          {event.event_name}
        </span>
      </div>
    </Table.Cell>
    <Table.Cell className="text-sm text-gray-500">
      {formatTimeAgo(event.last_seen)}
    </Table.Cell>
    <Table.Cell>
      <div className="flex gap-1">
        <Button
          size="xs"
          color="light"
          onClick={() => onPromote(event.id)}
          disabled={isPromoting || isDismissing}
          isProcessing={isPromoting}
          aria-label={`Track ${event.event_name}`}
        >
          {!isPromoting && <HiCheck className="mr-1 h-3 w-3" />}
          Track
        </Button>
        <Button
          size="xs"
          color="light"
          onClick={() => onDismiss(event.id)}
          disabled={isPromoting || isDismissing}
          isProcessing={isDismissing}
          aria-label={`Dismiss ${event.event_name}`}
        >
          {!isDismissing && <HiX className="mr-1 h-3 w-3" />}
          Dismiss
        </Button>
      </div>
    </Table.Cell>
  </Table.Row>
);
